import { GPSLocation } from '../models/GPSLocation';
import { IDictionary } from '../models/IDictionary';
import { MachineKey } from '../models/MachineKey';
import { MachineMarker } from '../models/mqttPayload/MachineMarkerMessage';
import { defineStore } from 'pinia';
import { storeKey } from './version';

export const useMachineStore = defineStore('machine', {
  state: () => ({
    machines: [] as Array<MachineKey>,
    selectedMachine: null as MachineKey | null,
    selectedMachineMarker: null as MachineMarker | null,
    selectedMachineIndex: null as number | null,
    isRecordingPlannedPath: false,
    isRecordingPaused: false,
    recordedPathPoints: [] as { loc: GPSLocation; timestamp: Date }[],
    sectionView: false,
    showLookahead: false
  }),
  getters: {
    currentMachineIndex(): number | null {
      return this.selectedMachineIndex;
    },
    availableMachines(): Array<MachineKey> {
      return this.machines;
    },
    machine(): MachineKey | null {
      return this.selectedMachine;
    },
    isRecording(): boolean {
      return this.isRecordingPlannedPath;
    },
    machineName(): string | undefined {
      return this.selectedMachine?.MachineName == null
        ? this.selectedMachine?.machineId
        : this.selectedMachine?.MachineName;
    },
    applicationCache(): IDictionary<string> {
      if (this.selectedMachineMarker?.RosApplicationCache == null) return {};
      return this.selectedMachineMarker.RosApplicationCache;
    },
    uiCache(): IDictionary<string> {
      if (this.selectedMachineMarker?.UIApplicationCache == null) return {};
      return this.selectedMachineMarker.UIApplicationCache;
    },
    rtkState(): boolean {
      if (this.selectedMachineMarker == null) return false;
      return this.selectedMachineMarker.GPSFixStatus == 4;
    },
    machineLocation(): GPSLocation | null {
      if (this.selectedMachineMarker?.Location == null) return null;
      return this.selectedMachineMarker.Location.Location;
    },
    machineLatitude(): number {
      if (this.selectedMachineMarker?.Location == null) return 0;
      return this.selectedMachineMarker.Location.Location.Latitude;
    },
    machineLongitude(): number {
      if (this.selectedMachineMarker?.Location == null) return 0;
      return this.selectedMachineMarker.Location.Location.Longitude;
    },
    machineBearing(): number {
      if (this.selectedMachineMarker?.Location == null) return 0;
      return Math.round(this.selectedMachineMarker.Location.Location.Bearing);
    },
    machineSpeed(): number {
      if (this.selectedMachineMarker?.Location == null) return 0;
      return Math.round(this.selectedMachineMarker.Location.Location.Speed);
    },
    isPathRecordingPaused(): boolean {
      return this.isRecordingPaused;
    },
    recordedPath(): GPSLocation[] {
      return this.recordedPathPoints.map((x) => x.loc);
    },
    getSectionView(): boolean {
      return this.sectionView;
    },
    getShowLookahead(): boolean {
      return this.showLookahead;
    }
  },
  actions: {
    setSelectedMachine(machine: MachineKey | null) {
      this.selectedMachine = machine;
    },
    setSelectedMachineMarker(marker: MachineMarker | null) {
      this.selectedMachineMarker = marker;
    },
    setSelectedMachineIndex(index: number | null) {
      this.selectedMachineIndex = index;
    },
    setAvailableMachines(newMachines: Array<MachineKey>) {
      this.machines = newMachines;
    },
    setRecordPlannedPath(record: boolean) {
      this.isRecordingPlannedPath = record;
      if (!record) {
        this.isRecordingPaused = false;
        this.recordedPathPoints = [];
      }
    },
    setPausePathRecording(pause: boolean) {
      this.isRecordingPaused = pause;
    },
    pushNewRecordedPathPoint(data: { loc: GPSLocation; timestamp: Date }) {
      if (this.recordedPathPoints.findIndex((x) => x.timestamp == data.timestamp) == -1) {
        this.recordedPathPoints.push(data);
        this.recordedPathPoints.sort((a, b) => {
          return a.timestamp.getTime() - b.timestamp.getTime();
        });
      }
    },
    setSectionView(value: boolean) {
      this.sectionView = value;
    },
    setShowLookahead(value: boolean) {
      this.showLookahead = value;
    }
  },
  persist: { key: `${storeKey()}.machine` }
});
